import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import helpers from 'helpers'
import useSnackbar from 'hooks/useSnackbar'
import * as service from 'service'

const useActivityModalForm = ({
  open,
  activity,
  fullPage,
  validationSchema,
}) => {
  const [formReady, setFormReady] = useState(false)
  const [fetchedTask, setFetchedTask] = useState(null)
  const [loadingTask, setLoadingTask] = useState(false)
  const [fetchedComments, setFetchedComments] = useState(null)
  const [isMultiDepartment, setIsMultiDepartment] = useState(false)

  const snackbar = useSnackbar()

  const isEdit = !!activity?.id
  const formMethods = useForm({
    validationSchema,
    defaultValues: {
      name: '',
      reason: '',
      description: '',
      responsibleIds: [],
      priority: '',
      kanbanColumnId: '',
      departmentIds: [],
      dueDate: null,
      attachments: null,
    },
  })

  const { reset } = formMethods
  const initialValuesRef = useRef(null)

  const resetWithSnapshot = (values) => {
    reset(values)
    initialValuesRef.current = {
      ...values,
      responsibleIds: (values.responsibleIds || [])
        .map((user) => user.id)
        .sort()
        .join(','),
      departmentIds: (values.departmentIds || [])
        .map((department) => department.id)
        .sort()
        .join(','),
      dueDate: values.dueDate || null,
    }
  }

  useEffect(() => {
    setFormReady(false)
    if (!open) return
    if (isEdit && activity?.id) {
      if (fullPage) {
        const multi = (activity.departments?.length || 0) > 1
        setIsMultiDepartment(multi)
        resetWithSnapshot({
          name: activity.name || '',
          reason: activity.reason || '',
          description: activity.description || '',
          responsibleIds: activity.responsibles || [],
          priority: activity.priority || '',
          kanbanColumnId: activity.kanbanColumnId || '',
          departmentIds: activity.departments || [],
          dueDate: activity.dueDate || null,
          attachments: activity.attachments?.length
            ? activity.attachments
            : null,
        })
        setFormReady(true)
        return
      }
      let cancelled = false
      const load = async () => {
        setLoadingTask(true)
        try {
          const res = await service.dponet.kanbanTasks.get({
            taskId: activity.id,
            include_comments: true,
          })
          const task = res?.data?.task
          if (!task || cancelled) return
          const multi = (task.departments?.length || 0) > 1
          setFetchedTask(task)
          setFetchedComments(task.taskComments || [])
          setIsMultiDepartment(multi)
          resetWithSnapshot({
            name: task.name || '',
            reason: task.reason || '',
            description: task.description || '',
            responsibleIds: task.responsibles || [],
            priority: task.priority || '',
            kanbanColumnId: task.kanbanColumnId || '',
            departmentIds: task.departments || [],
            dueDate: task.dueDate || null,
            attachments: task.attachments?.length ? task.attachments : null,
          })
        } catch (error) {
          if (!cancelled) {
            snackbar.open({
              message: helpers.formatters.errorMessage(error),
              variant: 'error',
            })
          }
        } finally {
          if (!cancelled) {
            setLoadingTask(false)
            setFormReady(true)
          }
        }
      }
      load()
      return () => {
        cancelled = true
      }
    } else {
      setIsMultiDepartment(false)
      initialValuesRef.current = null
      reset({
        name: '',
        reason: '',
        description: '',
        responsibleIds: [],
        priority: '',
        kanbanColumnId: activity?.kanbanColumnId || '',
        departmentIds: [],
        dueDate: null,
        attachments: null,
      })
      setFormReady(true)
    }
  }, [open, activity?.id]) // eslint-disable-line

  return {
    formMethods,
    initialValuesRef,
    isMultiDepartment,
    fetchedTask,
    fetchedComments,
    loadingTask,
    formReady,
  }
}

export default useActivityModalForm
